import { useRef, useState } from 'react'

import { VIEWBOX } from './boxTruckZones'
import { denormalize, hitTestZone, normalize } from './hitTest'

export { hitTestZone } from './hitTest'

/**
 * Схема кузова с отметками повреждений.
 *
 * Презентационный компонент: не ходит в сеть и ничего не сохраняет.
 * Наверх отдаёт только «водитель ткнул в такую-то зону в такой-то
 * точке» — решение, что с этим делать, принимает экран.
 *
 * ── Зум сделан через viewBox, а не через CSS transform ────────────
 *
 * Соблазн — навесить transform: scale() на контейнер. Тогда обратный
 * пересчёт координат касания зависит от матрицы трансформации,
 * а на телефоне ещё и от масштаба страницы. Меняя viewBox, мы
 * оставляем пересчёт линейным и честным: точка касания переводится
 * в единицы схемы одним умножением, и записанные координаты
 * не зависят от того, был ли включён зум.
 *
 * ── Отметка не знает своей проекции ───────────────────────────────
 *
 * В базе у точки лежат zone_id и нормализованные координаты, но нет
 * проекции. Для большинства зон этого достаточно — зона встречается
 * один раз. Но крыша кабины видна и спереди, и сверху, зеркало —
 * спереди и сбоку: это одна деталь с одним id. Проекцию для отрисовки
 * выводим из координат: точка ставилась внутри прямоугольника зоны,
 * а прямоугольники в разных проекциях не совпадают. Так удалось
 * не добавлять в схему БД колонку ради одной только отрисовки.
 */

const MARK_RADIUS = 9

function markBelongsHere(mark, zones) {
  const zone = zones.find((zn) => zn.id === mark.zone_id)
  if (!zone) return false
  const p = denormalize(mark.x, mark.y, VIEWBOX)
  const r = zone.rect
  // Допуск в пиксель viewBox: координаты пришли через float и округление.
  const pad = 1
  return (
    p.x >= r.x - pad && p.x <= r.x + r.w + pad &&
    p.y >= r.y - pad && p.y <= r.y + r.h + pad
  )
}

export function DamageSchema({
  projections,
  activeKey,
  onSelectProjection,
  /** Отметки: { client_mark_id | id, zone_id, x, y, status } */
  marks = [],
  onZoneTap,
  onMarkTap,
  loading = false,
  error = null,
  disabled = false,
}) {
  const svgRef = useRef(null)
  const [box, setBox] = useState({ x: 0, y: 0, w: VIEWBOX.width, h: VIEWBOX.height })
  const [focusZone, setFocusZone] = useState(null)
  const gesture = useRef(null)

  const projection = projections?.find((p) => p.key === activeKey) || projections?.[0]
  const zones = projection?.zones || []
  const visibleMarks = marks.filter((m) => markBelongsHere(m, zones))
  const zoomed = box.w < VIEWBOX.width - 0.5

  // ── Перевод касания в единицы схемы ───────────────────────────────
  // Контейнер держит то же соотношение сторон, что и viewBox (задано
  // в CSS через aspect-ratio), поэтому перевод — простая пропорция.
  const toSchema = (clientX, clientY) => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect || !rect.width) return null
    return {
      x: box.x + ((clientX - rect.left) / rect.width) * box.w,
      y: box.y + ((clientY - rect.top) / rect.height) * box.h,
    }
  }

  const clamp = (next) => {
    const w = Math.min(VIEWBOX.width, Math.max(VIEWBOX.width / 4, next.w))
    const h = w * (VIEWBOX.height / VIEWBOX.width)
    return {
      w, h,
      x: Math.min(Math.max(0, next.x), VIEWBOX.width - w),
      y: Math.min(Math.max(0, next.y), VIEWBOX.height - h),
    }
  }

  const resetZoom = () => setBox({ x: 0, y: 0, w: VIEWBOX.width, h: VIEWBOX.height })

  // ── Жесты ─────────────────────────────────────────────────────────
  const pinchDistance = (touches) =>
    Math.hypot(touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY)

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      gesture.current = { type: 'pinch', start: pinchDistance(e.touches), box }
    } else if (e.touches.length === 1 && zoomed) {
      gesture.current = {
        type: 'pan', x: e.touches[0].clientX, y: e.touches[0].clientY, box,
      }
    }
  }

  const onTouchMove = (e) => {
    const g = gesture.current
    if (!g) return
    if (g.type === 'pinch' && e.touches.length === 2) {
      const ratio = g.start / Math.max(1, pinchDistance(e.touches))
      const w = g.box.w * ratio
      // Масштабируем от центра: иначе схема уползает из-под пальцев.
      const cx = g.box.x + g.box.w / 2
      const cy = g.box.y + g.box.h / 2
      const h = w * (VIEWBOX.height / VIEWBOX.width)
      setBox(clamp({ w, h, x: cx - w / 2, y: cy - h / 2 }))
    } else if (g.type === 'pan' && e.touches.length === 1) {
      const rect = svgRef.current?.getBoundingClientRect()
      if (!rect?.width) return
      const dx = ((e.touches[0].clientX - g.x) / rect.width) * g.box.w
      const dy = ((e.touches[0].clientY - g.y) / rect.height) * g.box.h
      setBox(clamp({ ...g.box, x: g.box.x - dx, y: g.box.y - dy }))
    }
  }

  const onTouchEnd = () => { gesture.current = null }

  // ── Нажатие ───────────────────────────────────────────────────────
  const handleClick = (e) => {
    if (disabled) return
    // Панорамирование не должно ставить точку.
    if (gesture.current) return
    const point = toSchema(e.clientX, e.clientY)
    if (!point) return
    // Порог нажатия — в единицах схемы, поэтому при зуме он
    // пропорционально уменьшается: чем крупнее, тем точнее.
    const minHit = (box.w / VIEWBOX.width) * 28
    const zone = hitTestZone(zones, point.x, point.y, minHit)
    if (!zone) return
    onZoneTap?.(zone, normalize(point.x, point.y, VIEWBOX))
  }

  const handleZoneKey = (zone) => (e) => {
    if (disabled) return
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    // С клавиатуры точка ставится в центр зоны: точнее указать нечем,
    // а центр — единственный предсказуемый выбор.
    const r = zone.rect
    onZoneTap?.(zone, normalize(r.x + r.w / 2, r.y + r.h / 2, VIEWBOX))
  }

  if (error) {
    return (
      <div className="damage-schema damage-schema--message" role="alert">
        <p className="damage-schema__message-title">Схема не загрузилась</p>
        <p className="damage-schema__message-text">{error}</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="damage-schema damage-schema--message" aria-busy="true">
        <span className="spinner" />
        <p className="damage-schema__message-text">Загружаем схему кузова</p>
      </div>
    )
  }

  if (!projection) {
    return (
      <div className="damage-schema damage-schema--message">
        <p className="damage-schema__message-title">Схема недоступна</p>
        <p className="damage-schema__message-text">
          Для этого типа кузова схема повреждений пока не нарисована.
        </p>
      </div>
    )
  }

  return (
    <div className="damage-schema">
      <div className="damage-schema__tabs" role="tablist" aria-label="Проекции кузова">
        {projections.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={p.key === projection.key}
            className={`damage-schema__tab${p.key === projection.key ? ' is-active' : ''}`}
            onClick={() => { onSelectProjection?.(p.key); resetZoom() }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="damage-schema__stage">
        <svg
          ref={svgRef}
          className="damage-schema__svg"
          viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}
          role="group"
          aria-label={`Схема кузова, проекция: ${projection.label}`}
          onClick={handleClick}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {zones.map((zone) => (
            <rect
              key={zone.id}
              className={`damage-zone${focusZone === zone.id ? ' is-focus' : ''}`}
              x={zone.rect.x}
              y={zone.rect.y}
              width={zone.rect.w}
              height={zone.rect.h}
              rx="3"
              tabIndex={disabled ? -1 : 0}
              role="button"
              aria-label={zone.label}
              onFocus={() => setFocusZone(zone.id)}
              onBlur={() => setFocusZone(null)}
              onKeyDown={handleZoneKey(zone)}
            />
          ))}

          {visibleMarks.map((mark) => {
            const p = denormalize(mark.x, mark.y, VIEWBOX)
            const isNew = mark.status === 'new'
            return (
              <g
                key={mark.client_mark_id || mark.id}
                className={`damage-mark ${isNew ? 'damage-mark--new' : 'damage-mark--existing'}`}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-label={isNew ? 'Новое повреждение' : 'Ранее зафиксированное повреждение'}
                onClick={(e) => { e.stopPropagation(); onMarkTap?.(mark) }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onMarkTap?.(mark) }
                }}
              >
                <circle className="damage-mark__halo" cx={p.x} cy={p.y} r={MARK_RADIUS + 5} />
                <circle className="damage-mark__dot" cx={p.x} cy={p.y} r={MARK_RADIUS} />
              </g>
            )
          })}
        </svg>

        {zoomed && (
          <button type="button" className="damage-schema__reset" onClick={resetZoom}>
            Показать целиком
          </button>
        )}
      </div>

      <p className="damage-schema__hint">
        {disabled
          ? 'Осмотр завершён — схема доступна только для просмотра.'
          : 'Нажмите на деталь, где есть повреждение. Двумя пальцами — увеличить.'}
      </p>
    </div>
  )
}
